<template>
  <img
    v-if="showImg"
    class="avatar"
    :class="{ [imgSizeClass]: true, grayscale: disabled }"
    :src="user[avatarAttribute]?.default"
    alt="User avatar"
    @error="useFallbackUrl"
  />
  <span
    v-else-if="hasInitials"
    class="inline-flex items-center justify-center rounded-full shrink-0"
    :class="[
      imgSizeClass,
      { 'bg-blue-100 text-blue-700': !disabled, 'bg-gray-300 text-gray-700': disabled },
    ]"
  >
    <span class="font-medium leading-none" :class="textSizeClass">{{
      initials.toUpperCase()
    }}</span>
  </span>
  <img v-else class="avatar" :class="imgSizeClass" :src="avatarPlaceholder" alt="User avatar" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import avatarPlaceholder from '@/assets/img/avatar_placeholder.png'

interface User {
  [key: string]: unknown
  first_name?: string
  last_name?: string
  full_name?: string
}

interface Props {
  user: User
  size?: 'default' | 'small' | 'feed' | 'large' | 'big'
  disabled?: boolean
  avatarAttribute?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'default',
  disabled: false,
  avatarAttribute: 'avatar',
})

const hasAvatar = computed(() => {
  const avatarValue = props.user[props.avatarAttribute]
  if (!avatarValue) {
    return false
  }
  if (typeof avatarValue === 'object' && avatarValue !== null) {
    const avatar = avatarValue as { default?: string; original?: string }
    return !!avatar.default || !!avatar.original
  } else {
    return !!avatarValue
  }
})

const hasInitials = computed(() => {
  return (!!props.user.first_name && !!props.user.last_name) || !!props.user.full_name
})

const showImg = ref(hasAvatar.value)

const initials = computed(() => {
  if (props.user?.full_name) {
    const words = props.user.full_name.split(' ')
    return words[0][0] + words[1][0]
  } else {
    const first_name = props.user.first_name
    const last_name = props.user.last_name
    return (first_name?.[0] || '') + (last_name?.[0] || '')
  }
})

const imgSizeClass = computed(() => {
  return `avatar-${props.size}`
})

const textSizeClass = computed(() => {
  if (props.size === 'default') {
    return ''
  } else if (props.size === 'small') {
    return 'text-xs'
  } else if (props.size === 'large') {
    return 'text-lg'
  } else {
    return 'text-2xl'
  }
})

const useFallbackUrl = (event: Event) => {
  const target = event.target as HTMLImageElement
  // use fallback url if first one failed to load
  const avatarValue = props.user[props.avatarAttribute]
  if (avatarValue && typeof avatarValue === 'object' && avatarValue !== null) {
    const avatar = avatarValue as { original?: string }
    const defaultUrl = avatar.original
    if (defaultUrl && !target.src.endsWith(defaultUrl)) {
      target.src = defaultUrl
    } else {
      // if original image failed to load display initials
      showImg.value = false
    }
  } else {
    // if no fallback available, display initials
    showImg.value = false
  }
}
</script>
